import type { DatasetUploadFailure } from "@/api/datasetUpload";
import { storageBackendErrorText } from "@/components/dataset/storageBackendText";
import { isHttpClientError } from "@/http";
import { UploadFileInterruptedError, UploadFileNotSentError } from "./putPool";

// backend upload.* 에러 코드를 FE 분류와 사용자 메시지로 바꾸는 단일 소유자.
//
// Owns:
// - 코드별 사용자 문구(한글)와 잡 수준 분류(blocked 판정 등). 테이블 UI 의 실패 stage 표시는
//   datasetUploadFormatters 가 따로 소유한다(표시 포맷과 실행 분류를 분리한다).
//
// Does not own:
// - HTTP 오류 정규화(HttpClientError, @/http/httpError), 전송 재시도 분류(putPool).

export const uploadBlockedMessage =
  "다른 브라우저에서 이 Dataset을 업로드 중입니다. 해당 업로드가 끝나거나 만료된 뒤 다시 시도해 주세요.";

// 같은 staging 내용을 다시 처리해도 같은 결과가 나오는 실패다. 입력 구조·경로 문제와, Azure 정본
// 업로드에서만 나오는 두 코드가 여기에 든다.
const SOURCE_CHANGE_CODES: ReadonlySet<string> = new Set([
  "catalog_path_too_long",
  "no_matching_schema",
  // Azure 정본은 v0(또는 v0·v1 혼재) 형식을 받지 않는다. v1 로 바꿔 다시 올려야 한다.
  "v0_storage_backend_unsupported",
  // staging 의 파일에 저장소 체크섬이 없다. Azure 는 요청 1개로 올린 blob 에만 체크섬을 만든다.
  "checksum_missing",
]);

// 입력 구조·경로 문제는 같은 파일 재처리로 해결되지 않는다. 목록·창·엔진이 함께
// 폴더를 다시 선택하는 재업로드로 안내한다.
export function requiresUploadSourceChange(
  failure: DatasetUploadFailure | null | undefined,
): boolean {
  return failure != null && SOURCE_CHANGE_CODES.has(failure.code);
}

// 다른 브라우저가 lease 를 점유하거나 인수했다. 잡을 blocked 로 설정한다.
export function isUploadLeaseHeld(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "upload.lease.held";
}

// complete 의 상한 초과(413). 서버 상태가 바뀌지 않으므로 staged 파일을 정리한 뒤 재시도할 수 있다.
export function isCompleteOverLimit(error: unknown): boolean {
  return (
    isHttpClientError(error) &&
    (error.code === "upload.staged_too_large" || error.code === "upload.files.too_many") &&
    error.status === 413
  );
}

// complete 의 기대값 불일치(409). 서버의 staging 실측이 본문의 기대 파일 수 또는 총 바이트와 다르다.
// 서버 상태는 바뀌지 않는다. 그래서 다른 파일을 찾아 다시 올린 뒤 재시도할 수 있다.
export function isCompleteStagingMismatch(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "upload.complete.staging_mismatch";
}

// 서버 에러 코드를 사용자 메시지로 매핑한다. 매핑이 없으면 null 이다(호출부가 일반 메시지로 폴백한다).
export function uploadCodedErrorMessage(error: unknown): string | null {
  if (!isHttpClientError(error)) {
    return null;
  }
  switch (error.code) {
    case "upload.lease.held":
      return uploadBlockedMessage;
    case "upload.display_name.conflict":
      return "같은 이름의 Dataset 업로드가 이미 존재합니다. 이름을 바꾸거나 해당 업로드를 확인해 주세요.";
    case "upload.files.invalid_path":
      // FE 사전 검증(relativePath.ts)이 1차 방어라 정상 흐름에서는 여기에 오지 않는다(규칙 드리프트 신호).
      return "업로드할 수 없는 파일 경로가 있습니다. 폴더 구성을 확인해 주세요.";
    case "upload.location.unknown":
      // 설정 폼이 서버 목록에서만 고르게 하므로 정상 흐름에서는 여기에 오지 않는다(카탈로그 드리프트
      // 신호). 창을 다시 열면 목록을 새로 받으므로 그 방법을 안내한다.
      return "선택한 업로드 서버 위치를 찾을 수 없습니다. 창을 닫고 다시 열어 주세요.";
    case "upload.staged_too_large":
      return "업로드 용량이 허용 한도를 초과했습니다.";
    case "upload.files.too_many":
      return "업로드 파일 수가 허용 한도를 초과했습니다.";
    case "upload.complete.empty_staging":
      return "업로드된 파일이 없어 완료할 수 없습니다.";
    case "upload.complete.staging_mismatch":
      return "서버에 올라간 파일이 선택한 폴더와 다릅니다. 다시 시도하면 없거나 크기가 다른 파일을 다시 올립니다.";
    case "upload.cancel.terminal":
      return "이미 종료된 업로드라 취소할 수 없습니다.";
    case "upload.cancel.in_progress":
      return "서버 처리가 시작된 업로드는 취소할 수 없습니다.";
    case "upload.cancel.forbidden":
      return "다른 사용자가 시작한 업로드는 취소할 수 없습니다.";
    case "upload.delete.forbidden":
      return "다른 사용자가 시작한 업로드 기록은 삭제할 수 없습니다.";
    case "upload.delete.invalid_status":
      // 목록이 오래된 동안 잡이 다시 진행된 경우. 상태를 새로 받아야 판단할 수 있다.
      return "이 업로드는 삭제할 수 있는 상태가 아닙니다. 목록을 새로고침해 확인해 주세요.";
    case "upload.reset.invalid_status":
      return "이 업로드는 다시 시작할 수 없습니다(이미 완료되었거나 처리 중입니다).";
    case "upload.reprocess.invalid_status":
      return "업로드 없이 재처리할 수 없는 상태입니다. 폴더를 다시 선택해 재업로드해 주세요.";
    case "upload.not_found":
      return "업로드 작업을 찾을 수 없습니다(삭제되었을 수 있습니다).";
    case "label.invalid_reserved_value":
      return "선택한 라벨 값이 유효하지 않습니다. 값을 다시 확인해 주세요.";
    case "label.missing_required":
      // 설정 폼의 필수 표시와 시작 게이트가 1차 방어라 신규 업로드는 여기에 오지 않는다(계약 드리프트
      // 신호). 필수 강제 이전에 만들어진 잡을 이어받아 완료할 때 나온다. 이어받기 경로에는 값을
      // 다시 고를 화면이 없어서 재업로드로 안내한다.
      return "필수 라벨이 선택되지 않아 업로드를 완료할 수 없습니다. 폴더를 다시 선택해 재업로드해 주세요.";
    case "dataset.data_origin_missing":
      // 설정 폼이 1차 방어라 신규 업로드는 여기에 오지 않는다. 행동 주체 도입 이전에 만들어진 잡을
      // 이어받아 완료할 때만 나온다. 이어받기 경로에는 값을 보낼 API 가 없어서 재업로드로 안내한다.
      return "행동 주체가 선언되지 않아 업로드를 완료할 수 없습니다. 폴더를 다시 선택해 재업로드해 주세요.";
    default:
      return null;
  }
}

const FALLBACK_MESSAGE = "Dataset 업로드 요청을 완료하지 못했습니다. 잠시 후 다시 시도해 주세요.";

// 업로드 흐름의 에러를 사용자 메시지로 바꾼다. 폴백 순서: 코드 매핑 → normalize 된 서버 메시지 →
// 일반 Error 메시지.
export function resolveUploadErrorMessage(error: unknown): string {
  if (error instanceof UploadFileInterruptedError) {
    return "다른 파일은 올라가는데 이 파일만 전송 중에 거듭 끊겼습니다. 회선이 불안정하거나 보안 장비가 큰 업로드를 막는지 확인해 주세요.";
  }
  if (error instanceof UploadFileNotSentError) {
    // 연결은 살아 있었다. 같은 File 로 다시 보내도 실패하면 폴더를 다시 골라야 한다.
    return "연결은 정상인데 이 파일을 보내지 못했습니다. 업로드 중에 파일이 바뀌었다면 폴더를 다시 선택해 이어서 올려 주세요.";
  }
  // 저장 위치 오류의 서버 문장은 영어다. 5xx 는 HTTP client 가 서버 문장을 버린다. 그래서 먼저 가른다.
  const storageBackendText = storageBackendErrorText(error);
  if (storageBackendText !== null) {
    return storageBackendText;
  }
  const coded = uploadCodedErrorMessage(error);
  if (coded !== null) {
    return coded;
  }
  if (isHttpClientError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return FALLBACK_MESSAGE;
}

// 워커 파이프라인 실패 단계 → 사용자 라벨(files 모드 상태 어휘. extracting 은 없다).
const FAILURE_STAGE_LABELS: Record<string, string> = {
  uploading: "업로드 검증",
  // import_attempts_exhausted 는 마지막으로 머물던 UploadStatus 를 단계로 보낸다. 큐 대기 중에
  // 재점유가 소진되면 uploaded 가 그대로 들어온다.
  uploaded: "처리 대기",
  fetching: "파일 수집",
  probing: "Dataset 확인",
  validating: "검증",
  migrating: "마이그레이션",
  publishing: "데이터 반영",
  unknown: "서버 처리",
};

// 워커 reason → 사용자 문구. 기준은 backend service/dataset_upload/contract/_failure.py 다.
// 스키마 불일치는 derive_failure 가 세부 원인에서 계산한다. 여기 없는 코드는
// formatUploadJobFailure 가 단계 문구로 폴백한다.
const FAILURE_CODE_MESSAGES: Record<string, string> = {
  // FETCHING: staging 수집과 manifest 구축.
  scratch_setup_failed: "처리 작업 공간을 준비하지 못했습니다.",
  manifest_build_failed: "업로드한 파일 목록을 만들지 못했습니다.",
  no_matching_schema: "지원하는 Dataset 폴더 구조와 일치하지 않습니다.",
  manifest_confirm_failed: "반입 재개점을 저장하지 못했습니다.",
  staging_tree_download_failed: "업로드한 파일을 서버로 가져오지 못했습니다.",
  source_tree_download_failed: "원본 파일을 서버로 가져오지 못했습니다.",
  source_empty: "업로드한 위치에 파일이 없습니다. 다시 업로드해 주세요.",
  v0_too_large: "구버전 형식은 변환 용량 상한을 넘어 처리할 수 없습니다. 나눠서 올려 주세요.",
  // 아래 둘은 Azure 정본 업로드에서만 나온다.
  v0_storage_backend_unsupported:
    "이 저장 위치는 구버전 형식을 받지 않습니다. v1 형식으로 바꿔 주세요.",
  checksum_missing:
    "저장소가 무결성 값을 만들지 못한 파일이 있습니다. 파일마다 요청 한 번으로 올려야 합니다.",
  // complete 이후 staging 내용이 변조되거나 유실된 경우(worker drift 검증). 재업로드만 해결 방법이다.
  staging_drift: "완료 이후 스테이징 내용이 바뀌어 처리를 중단했습니다. 다시 업로드해 주세요.",
  // 워커 연결 누락. 사용자가 고칠 수 없는 운영 문제다. `staging_downloader_unavailable` 은 서버가 더
  // 내지 않는다. 그 사유로 실패한 옛 잡이 목록에 남아 있어서 문구를 둔다.
  staging_downloader_unavailable: "서버 처리 구성이 준비되지 않았습니다. 운영자에게 문의해 주세요.",
  import_transfer_unavailable: "서버 처리 구성이 준비되지 않았습니다. 운영자에게 문의해 주세요.",
  storage_backend_not_configured:
    "서버에 이 저장 위치의 설정이 없습니다. 운영자에게 문의해 주세요.",
  // PROBING / VALIDATING / MIGRATING.
  probe_failed: "Dataset 구조를 확인하지 못했습니다.",
  submit_plan_failed: "복원 계획을 저장하지 못했습니다.",
  migrate_failed: "Dataset 형식 변환에 실패했습니다.",
  migration_advance_failed: "마이그레이션 단계 전환에 실패했습니다.",
  // PUBLISHING: meta 재구성, 청크 반복(copy → 채널 결손 판정 → commit), 카탈로그 확정.
  inventory_failed: "변환된 파일 목록을 만들지 못했습니다.",
  target_clear_failed: "이전 반입 결과를 정리하지 못했습니다.",
  chunk_copy_failed: "파일을 정본 저장소로 옮기지 못했습니다.",
  chunk_fetch_failed: "meta 재구성에 쓸 파일을 정본 저장소에서 다시 읽는 데 실패했습니다.",
  meta_reconstruct_failed:
    "meta/info.json 을 만들지 못했습니다. 모든 채널 파일을 가진 에피소드가 없거나 그 파일이 손상되었습니다.",
  chunk_validate_failed: "Dataset 구간 검증에 실패했습니다.",
  chunk_commit_failed: "카탈로그 구간 반영에 실패했습니다.",
  extra_commit_failed: "부가 파일 반영에 실패했습니다.",
  v0_upload_failed: "변환한 파일을 올리지 못했습니다.",
  catalog_path_too_long: "Dataset 내부 파일 경로가 카탈로그의 허용 길이를 초과했습니다.",
  finalize_commit_failed: "카탈로그 반영을 완료하지 못했습니다.",
  // 단계와 관계없음.
  advance_status_failed: "처리 상태 보고에 실패했습니다.",
  import_attempts_exhausted: "재시도 횟수를 모두 써 처리를 중단했습니다.",
  worker_crashed: "서버 처리 중 예기치 못한 오류가 발생했습니다.",
  seed_missing: "업로드 대상 Dataset 정보를 찾지 못했습니다.",
};

// 전송 검증은 Dataset 구조 검증을 보장하지 않는다. 입력 수정이 필요한 실패에는
// 재업로드를 안내하고, 그 외 실패만 일시적 서버 오류일 때 재처리할 수 있다고 설명한다.
export function formatUploadJobFailure(
  failure: DatasetUploadFailure | null,
  uploadVerified: boolean,
): string {
  const action =
    failure?.code === "catalog_path_too_long"
      ? "폴더 안의 파일 경로를 줄인 뒤 폴더를 다시 선택해 재업로드해 주세요."
      : failure?.code === "no_matching_schema" || failure?.code === "meta_reconstruct_failed"
        ? "같은 파일로 재처리해도 해결되지 않습니다. 원본의 필수 폴더와 파일 구성을 수정한 뒤 업로드 현황에서 재업로드해 주세요."
        : failure?.code === "v0_storage_backend_unsupported" || failure?.code === "checksum_missing"
          ? "같은 파일로 재처리해도 해결되지 않습니다. 업로드 현황에서 재업로드해 주세요."
          : uploadVerified
            ? "일시적인 서버 오류라면 재처리를 시도할 수 있습니다. 같은 오류가 반복되면 원본 파일을 확인해 주세요."
            : "폴더를 다시 선택해 재업로드해 주세요.";

  if (failure === null) {
    return uploadVerified
      ? "서버 처리 중 오류가 발생했습니다. " + action
      : "업로드 처리 중 오류가 발생했습니다. " + action;
  }

  const stage = FAILURE_STAGE_LABELS[failure.stage] ?? FAILURE_STAGE_LABELS.unknown;
  const reason =
    FAILURE_CODE_MESSAGES[failure.code] ?? "서버 처리 중 알 수 없는 오류가 발생했습니다.";
  return stage + " 단계에서 실패했습니다. " + reason + " " + action;
}
